import type { LearningExercise, SourceRef } from "../types";
import { makeCloze, makeMulti, makeOrdering, makeSingle } from "./legalExerciseFactory";

// EQE-Lernmodul: Paper F — Formerfordernisse & Anspruchs-Drafting (modulare EQE ab März 2025)
// Themen: R. 40 Anmeldetag, R. 42 Beschreibung, R. 43 Anspruchsform,
//         Art. 83 Ausführbarkeit, Art. 84 Klarheit/Stützung,
//         Art. 123(2)/(3) Erweiterung, Art. 76 Teilanmeldung,
//         Art. 87-89 Priorität, Art. 80 Anmeldetag vs. vollständige Anmeldung.
// pathId: "epu-eqe", mode: "ausbildung", reviewStatus (Items): "reviewed"
// Quellen-Basis: EPÜ 2020-Fassung (epo.org/en/legal/epc/2020/)
// ID-Bereich: eqe-paperf-001 … eqe-paperf-028

const pathId = "epu-eqe" as const;

const sources: Record<string, SourceRef> = {
  r40epc:   { id: "r40epc",   label: "R. 40 EPÜ",    url: "https://www.epo.org/en/legal/epc/2020/r40.html" },
  r42epc:   { id: "r42epc",   label: "R. 42 EPÜ",    url: "https://www.epo.org/en/legal/epc/2020/r42.html" },
  r43epc:   { id: "r43epc",   label: "R. 43 EPÜ",    url: "https://www.epo.org/en/legal/epc/2020/r43.html" },
  r56epc:   { id: "r56epc",   label: "R. 56 EPÜ",    url: "https://www.epo.org/en/legal/epc/2020/r56.html" },
  r58epc:   { id: "r58epc",   label: "R. 58 EPÜ",    url: "https://www.epo.org/en/legal/epc/2020/r58.html" },
  art80epc: { id: "art80epc", label: "Art. 80 EPÜ",  url: "https://www.epo.org/en/legal/epc/2020/a80.html" },
  art83epc: { id: "art83epc", label: "Art. 83 EPÜ",  url: "https://www.epo.org/en/legal/epc/2020/a83.html" },
  art84epc: { id: "art84epc", label: "Art. 84 EPÜ",  url: "https://www.epo.org/en/legal/epc/2020/a84.html" },
  art76epc: { id: "art76epc", label: "Art. 76 EPÜ",  url: "https://www.epo.org/en/legal/epc/2020/a76.html" },
  art87epc: { id: "art87epc", label: "Art. 87 EPÜ",  url: "https://www.epo.org/en/legal/epc/2020/a87.html" },
  art88epc: { id: "art88epc", label: "Art. 88 EPÜ",  url: "https://www.epo.org/en/legal/epc/2020/a88.html" },
  art123epc:{ id: "art123epc",label: "Art. 123 EPÜ", url: "https://www.epo.org/en/legal/epc/2020/a123.html" },
};

export const eqePaperF: LearningExercise[] = [

  // ─── R. 40 EPÜ — Anmeldetag-Minimalerfordernisse ─────────────────────────

  // 001 — single, level 1: Überblick R. 40(1)
  makeSingle(sources, {
    id: "eqe-paperf-001",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — R. 40 EPÜ Anmeldetag",
    title: "Minimalerfordernisse für den Anmeldetag (R. 40(1) EPÜ)",
    prompt:
      "Welche drei Mindestangaben muss eine Einreichung nach R. 40(1) EPÜ enthalten, damit das EPA ihr einen Anmeldetag zuerkennt?",
    options: [
      "(a) Hinweis auf ein europäisches Patent, (b) Angaben zur Identität des Anmelders, (c) Beschreibung oder Bezugnahme auf eine frühere Anmeldung — Ansprüche sind am Anmeldetag nicht zwingend erforderlich.",
      "(a) Hinweis auf ein europäisches Patent, (b) Patentansprüche, (c) Beschreibung — der Anmelder ist erst bei der formellen Prüfung anzugeben.",
      "(a) Name des Erfinders, (b) vollständige Beschreibung, (c) Ansprüche und Zusammenfassung.",
      "(a) Anmeldeformular, (b) Anmeldegebühr, (c) Beschreibung — ohne Gebühr wird kein Anmeldetag zugeteilt.",
    ],
    answer: 0,
    explanation:
      "R. 40(1) EPÜ nennt abschließend drei Minimalerfordernisse: (a) eine Angabe, dass ein europäisches Patent beantragt wird, (b) Angaben, die die Identität des Anmelders feststellen lassen, und (c) eine Beschreibung oder eine Bezugnahme auf eine frühere Anmeldung. Ansprüche sind am Anmeldetag ausdrücklich nicht erforderlich — sie können gemäß R. 40(1)(c) EPÜ nachgereicht werden. Die Anmeldegebühr beeinflusst den Anmeldetag nicht (sie hat andere Fristen, vgl. R. 38 EPÜ). Dieser Punkt ist ein klassischer Paper-F-Fallstrick.",
    sourceIds: ["r40epc"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "paperf", "anmeldetag", "r40"],
  }),

  // 002 — single, level 2 (Fallkonstellation): fehlende Ansprüche
  makeSingle(sources, {
    id: "eqe-paperf-002",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — R. 40 EPÜ Anmeldetag",
    title: "Fall: Anmeldung ohne Patentansprüche — Anmeldetag?",
    prompt:
      "Anmelder A reicht am 3. März eine Beschreibung und die Anmeldeformulare beim EPA ein, vergisst aber, Patentansprüche beizufügen. Teilt das EPA dieser Einreichung trotzdem einen Anmeldetag zu?",
    options: [
      "Nein — ohne Ansprüche entsteht kein Anmeldetag; A muss die Ansprüche gleichzeitig mit der Beschreibung einreichen.",
      "Ja — nach R. 40(1)(c) EPÜ reicht die Einreichung einer Beschreibung (oder Bezugnahme auf eine frühere Anmeldung) für den Anmeldetag aus; Ansprüche können nachgereicht werden.",
      "Ja, aber der Anmeldetag wird nur vorläufig zugeteilt und erlischt, wenn die Ansprüche nicht binnen einer Woche nachgereicht werden.",
      "Nein — ohne Ansprüche fehlt es an einer 'Offenbarung' im Sinne von Art. 83 EPÜ, sodass kein Anmeldetag anerkannt wird.",
    ],
    answer: 1,
    explanation:
      "R. 40(1)(c) EPÜ verlangt lediglich eine 'Beschreibung oder eine Bezugnahme auf eine frühere Anmeldung' — Patentansprüche sind am Anmeldetag keine Pflichtangabe. Das EPA teilt A daher trotz fehlender Ansprüche den Anmeldetag 3. März zu. Die Ansprüche sind auf Aufforderung des EPA innerhalb der nach R. 58 EPÜ gesetzten Frist (Beseitigung von Mängeln der Anmeldungsunterlagen) nachzureichen. Für Paper F zentral: Ansprüche ≠ Anmeldetag-Erfordernis.",
    sourceIds: ["r40epc", "r58epc", "art80epc"],
    levelNumber: 2,
    errorCategory: "Irrtum",
    skillTags: ["eqe", "paperf", "anmeldetag", "r40", "fallkonstellation"],
  }),

  // 003 — single, level 2: Bezugnahme auf frühere Anmeldung (R. 40(1)(c))
  makeSingle(sources, {
    id: "eqe-paperf-003",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — R. 40 EPÜ Anmeldetag",
    title: "Bezugnahme auf frühere Anmeldung als Beschreibungsersatz",
    prompt:
      "Anmelderin B verweist in ihrer Einreichung anstatt einer vollständigen Beschreibung lediglich auf eine frühere PCT-Anmeldung. Welche Anforderung stellt R. 40(1)(c) EPÜ für diese Variante?",
    options: [
      "Die Bezugnahme auf die frühere Anmeldung genügt für den Anmeldetag; allerdings muss das Aktenzeichen der früheren Anmeldung angegeben werden und eine beglaubigte Abschrift innerhalb der vorgesehenen Frist nachgereicht werden.",
      "Eine Bezugnahme auf eine frühere Anmeldung ist grundsätzlich unzulässig; stets ist die vollständige Beschreibung einzureichen.",
      "Die Bezugnahme genügt nur dann, wenn die frühere Anmeldung beim EPA selbst anhängig ist.",
      "Die Bezugnahme auf eine frühere Anmeldung ersetzt nur die Beschreibung, nicht aber die Ansprüche, die zwingend am Anmeldetag vorliegen müssen.",
    ],
    answer: 0,
    explanation:
      "R. 40(1)(c) EPÜ lässt ausdrücklich die Bezugnahme auf eine frühere Anmeldung als Ersatz für die Einreichung einer vollständigen Beschreibung zu. Gemäß R. 40(3) EPÜ muss dabei das Aktenzeichen der früheren Anmeldung angegeben und eine beglaubigte Abschrift fristgerecht (innerhalb von zwei Monaten, R. 40(3) EPÜ) nachgereicht werden, sofern die Anmeldung nicht beim EPA selbst vorliegt. Entscheidend: Option D ist falsch, weil Ansprüche generell am Anmeldetag nicht zwingend erforderlich sind.",
    sourceIds: ["r40epc"],
    levelNumber: 2,
    errorCategory: "Ausnahme",
    skillTags: ["eqe", "paperf", "anmeldetag", "r40", "bezugnahme"],
  }),

  // ─── R. 42 EPÜ — Inhalt der Beschreibung ─────────────────────────────────

  // 004 — multi, level 2: Pflichtbestandteile der Beschreibung
  makeMulti(sources, {
    id: "eqe-paperf-004",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — R. 42 EPÜ Beschreibung",
    title: "Pflichtbestandteile der Beschreibung nach R. 42 EPÜ (Mehrfachauswahl)",
    prompt:
      "Welche der folgenden Angaben müssen nach R. 42(1) EPÜ in der Beschreibung einer europäischen Patentanmeldung enthalten sein? (Mehrere Antworten möglich.)",
    options: [
      "Angabe des technischen Gebiets, auf das sich die Erfindung bezieht.",
      "Kurzbeschreibung der Figuren / Zeichnungen (sofern Zeichnungen vorhanden).",
      "Name und Adresse des bevollmächtigten Vertreters.",
      "Beschreibung mindestens eines Weges zur Ausführung der beanspruchten Erfindung (mit Beispielen, soweit geeignet).",
    ],
    answers: [0, 1, 3],
    explanation:
      "R. 42(1) EPÜ schreibt vor, dass die Beschreibung u.a. enthält: (a) das technische Gebiet (R. 42(1)(a)), (b) den Stand der Technik, (c) die Offenbarung der Erfindung, (d) eine Kurzbeschreibung der Figuren (soweit Zeichnungen vorliegen) (R. 42(1)(d)), sowie (e) mindestens einen Ausführungsweg (R. 42(1)(e)). Name und Adresse des Vertreters sind Angaben der formellen Anmeldung (vgl. R. 41 EPÜ), aber kein Pflichtbestandteil der Beschreibung selbst.",
    sourceIds: ["r42epc"],
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["eqe", "paperf", "beschreibung", "r42"],
  }),

  // 005 — single, level 3 (Fallkonstellation): Stand der Technik in der Beschreibung
  makeSingle(sources, {
    id: "eqe-paperf-005",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — R. 42 EPÜ Beschreibung",
    title: "Fall: Fehlende Stand-der-Technik-Würdigung in der Beschreibung",
    prompt:
      "Die Beschreibung einer eingereichten europäischen Anmeldung enthält kein Kapitel zum Stand der Technik. Welche Konsequenz hat dieses Fehlen im Prüfungsverfahren?",
    options: [
      "Das EPA muss die Anmeldung sofort zurückweisen, da das Fehlen des Stands der Technik einen nicht behebbaren Mangel darstellt.",
      "Das EPA wird einen Mangelbescheid nach R. 42(1)(b) EPÜ erteilen und dem Anmelder Gelegenheit geben, die Beschreibung durch ein entsprechendes Kapitel zu ergänzen.",
      "Das Fehlen ist unerheblich, weil der Stand der Technik aus dem Recherchenbericht des EPA ersichtlich wird.",
      "Das Fehlen begründet automatisch einen Mangel nach Art. 83 EPÜ (Ausführbarkeit), weil der Fachmann die technische Aufgabe nicht erkennen kann.",
    ],
    answer: 1,
    explanation:
      "R. 42(1)(b) EPÜ verlangt, dass die Beschreibung den relevanten Stand der Technik würdigt. Fehlt dieser Abschnitt, erteilt das EPA im Prüfungsverfahren einen Mangelbescheid und fordert den Anmelder auf, die Beschreibung entsprechend zu ergänzen. Die Anmeldung wird nicht sofort zurückgewiesen. Das Fehlen ist auch kein Art.-83-EPÜ-Mangel, da Art. 83 EPÜ die Nacharbeitbarkeit der Erfindung selbst betrifft, nicht die formelle Gliederung der Beschreibung.",
    sourceIds: ["r42epc", "art83epc"],
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["eqe", "paperf", "beschreibung", "r42", "fallkonstellation"],
  }),

  // ─── R. 43 EPÜ — Anspruchsform ────────────────────────────────────────────

  // 006 — single, level 1: Oberbegriff + kennzeichnender Teil
  makeSingle(sources, {
    id: "eqe-paperf-006",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — R. 43 EPÜ Anspruchsform",
    title: "Anspruchsaufbau: Oberbegriff und kennzeichnender Teil",
    prompt:
      "Nach R. 43(1) EPÜ soll ein unabhängiger Anspruch aus zwei Teilen bestehen. Wie lauten diese, und was enthält jeder Teil?",
    options: [
      "Erster Teil (Oberbegriff): die bekannten Merkmale der Erfindung. Zweiter Teil (kennzeichnender Teil, eingeleitet durch 'dadurch gekennzeichnet, dass'): die neuen, schutzwürdigen Merkmale gegenüber dem Stand der Technik.",
      "Erster Teil (Zweck): die technische Aufgabe. Zweiter Teil (Lösung): die beanspruchten Merkmale.",
      "Erster Teil (Kategorie): ob Erzeugnisanspruch oder Verfahrensanspruch. Zweiter Teil (Ausführungsform): eine bevorzugte Ausführungsform.",
      "Erster Teil (Stand der Technik): vollständige Beschreibung des nächsten Standes der Technik. Zweiter Teil (Unterschied): nur das einzige unterscheidende Merkmal.",
    ],
    answer: 0,
    explanation:
      "R. 43(1) EPÜ schreibt vor, dass ein Anspruch einen Oberbegriff enthält, der die bekannten Merkmale der Erfindung angibt, und einen kennzeichnenden Teil, der mit 'dadurch gekennzeichnet, dass' (oder gleichbedeutenden Wendungen) eingeleitet wird und die neuen, gegenüber dem Stand der Technik schutzwürdigen Merkmale enthält. Diese Zweiteilung dient der Transparenz hinsichtlich des Stands der Technik. Sie ist verpflichtend, wenn ein nächster Stand der Technik bekannt ist; andernfalls kann ein Anspruch im Ein-Teil-Format verfasst sein.",
    sourceIds: ["r43epc"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "paperf", "ansprueche", "r43", "oberbegriff"],
  }),

  // 007 — single, level 2: mehrere unabhängige Ansprüche derselben Kategorie
  makeSingle(sources, {
    id: "eqe-paperf-007",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — R. 43 EPÜ Anspruchsform",
    title: "Mehrere unabhängige Ansprüche derselben Kategorie (R. 43(2) EPÜ)",
    prompt:
      "Unter welchen Voraussetzungen sind nach R. 43(2) EPÜ mehrere unabhängige Ansprüche derselben Kategorie (z.B. zwei unabhängige Vorrichtungsansprüche) zulässig?",
    options: [
      "Grundsätzlich immer — das EPÜ schränkt die Anzahl unabhängiger Ansprüche nicht ein.",
      "Nur ausnahmsweise, nämlich wenn der Gegenstand der Anmeldung es sinnvoll erscheinen lässt, z.B. wenn mehrere miteinander in Wechselwirkung stehende Erfindungen beansprucht werden oder wenn Alternativlösungen vorliegen, die nicht durch einen einzigen Anspruch erfasst werden können.",
      "Grundsätzlich nie — pro Kategorie ist stets nur ein einziger unabhängiger Anspruch zulässig.",
      "Nur wenn jeder unabhängige Anspruch derselben Kategorie eine Priorität aus einer anderen Anmeldung beansprucht.",
    ],
    answer: 1,
    explanation:
      "R. 43(2) EPÜ erlaubt mehrere unabhängige Ansprüche derselben Kategorie nur ausnahmsweise, wenn der Gegenstand der Anmeldung es sinnvoll erscheinen lässt. Praktische Ausnahmen sind: (a) mehrere in Wechselwirkung stehende Produkte (z.B. Sender und Empfänger), (b) alternative Lösungen, die nicht sinnvoll in einem einzigen Anspruch zusammengefasst werden können, oder (c) verfahrens- und vorrichtungsbezogene Varianten. Die Ausnahme ist eng auszulegen; unbegründet doppelte unabhängige Ansprüche führen zu Einwänden.",
    sourceIds: ["r43epc"],
    levelNumber: 2,
    errorCategory: "Ausnahme",
    skillTags: ["eqe", "paperf", "ansprueche", "r43", "unabhaengig"],
  }),

  // 008 — single, level 3 (Fallkonstellation): Anspruchsaufbau beurteilen
  makeSingle(sources, {
    id: "eqe-paperf-008",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — R. 43 EPÜ Anspruchsform",
    title: "Fall: Fehler im Anspruchsaufbau identifizieren",
    prompt:
      "Anspruch 1 einer eingereichten Anmeldung lautet: 'Vorrichtung zum Aufbereiten von Trinkwasser, gekennzeichnet durch einen Filterkanal.' Anspruch 2 lautet: 'Vorrichtung nach Anspruch 1, die einen Filterkanal umfasst.' Welcher Fehler liegt vor?",
    options: [
      "Anspruch 2 ist ein unzulässiger zweiter unabhängiger Anspruch, weil er ohne Rückverweis verfasst ist.",
      "Anspruch 1 fehlt ein Oberbegriff mit bekannten Merkmalen (R. 43(1) EPÜ); Anspruch 2 ist ein abhängiger Anspruch, der lediglich dasselbe Merkmal wiederholt wie Anspruch 1, ohne ein zusätzliches schützenswürdiges Merkmal hinzuzufügen.",
      "Die Ansprüche sind in Ordnung; Anspruch 2 ist eine zulässige Alternativformulierung.",
      "Anspruch 1 ist ein Verfahrensanspruch, der nach R. 43 EPÜ nicht mit 'Vorrichtung' überschrieben werden darf.",
    ],
    answer: 1,
    explanation:
      "Anspruch 1 enthält keinen Oberbegriff mit bekannten Merkmalen (R. 43(1) EPÜ) und fügt den 'Filterkanal' sofort als einziges Merkmal im kennzeichnenden Teil ein — das ist formell mangelhaft, wenn ein Stand der Technik existiert. Anspruch 2 ist zwar formal als abhängiger Anspruch (Rückverweis 'nach Anspruch 1') formuliert, fügt aber kein weiteres Merkmal hinzu, sondern wiederholt nur den Filterkanal. Abhängige Ansprüche müssen nach R. 43(3) EPÜ mindestens ein zusätzliches Merkmal enthalten, das den Gegenstand des Hauptanspruchs weiter einschränkt.",
    sourceIds: ["r43epc"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["eqe", "paperf", "ansprueche", "r43", "fallkonstellation"],
  }),

  // ─── Art. 83 EPÜ — Ausführbarkeit ────────────────────────────────────────

  // 009 — single, level 1: Maßstab Ausführbarkeit
  makeSingle(sources, {
    id: "eqe-paperf-009",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Art. 83 EPÜ Ausführbarkeit",
    title: "Ausführbarkeit: Maßstab und Personenbezug",
    prompt:
      "Welchen Maßstab legt Art. 83 EPÜ an, wenn es darum geht, ob eine Erfindung ausreichend offenbart ist?",
    options: [
      "Die Erfindung muss so deutlich und vollständig offenbart sein, dass ein Fachmann sie ausführen kann — ohne unzumutbaren Aufwand (undue burden).",
      "Die Erfindung muss so offenbart sein, dass ein Laie sie nachbauen kann.",
      "Die Erfindung gilt als ausreichend offenbart, wenn mindestens ein Beispiel in der Beschreibung enthalten ist.",
      "Art. 83 EPÜ verlangt nur, dass die erfinderische Idee erkennbar ist; Ausführungsdetails können dem Fachmann überlassen bleiben.",
    ],
    answer: 0,
    explanation:
      "Art. 83 EPÜ schreibt vor, dass die europäische Patentanmeldung die Erfindung so deutlich und vollständig offenbaren muss, dass ein Fachmann sie ausführen kann. Der 'Fachmann' ist eine fiktive Durchschnittsperson auf dem relevanten technischen Gebiet. 'Undue burden' (unzumutbarer Aufwand) ist die Grenze: Wenn der Fachmann trotz normalem Fachwissen und üblichen Routineexperimenten die Erfindung nicht nacharbeiten kann, liegt ein Mangel vor. Ein einzelnes Beispiel kann ausreichen, muss es aber nicht — entscheidend ist die Nacharbeitbarkeit des gesamten beanspruchten Bereichs.",
    sourceIds: ["art83epc"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "paperf", "ausfuehrbarkeit", "art83"],
  }),

  // 010 — single, level 3 (Fallkonstellation): Art. 83 vs. Art. 84 abgrenzen
  makeSingle(sources, {
    id: "eqe-paperf-010",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Art. 83 / Art. 84 EPÜ Abgrenzung",
    title: "Fall: Mangel wegen fehlenden Beispiels — Art. 83 oder Art. 84?",
    prompt:
      "Anspruch 1 beansprucht ein Polymerwerkstoff mit einem 'verbesserten Schmelzpunkt'. Die Beschreibung enthält keine Beispiele und keine Angaben, wie der Schmelzpunkt gemessen oder verbessert wird. Der Prüfer möchte einen Einwand erheben. Welcher Einwand greift hier primär?",
    options: [
      "Ausschließlich Art. 84 EPÜ (Klarheit), weil 'verbesserter Schmelzpunkt' ein unklarer Begriff ist.",
      "Art. 83 EPÜ (Ausführbarkeit): Der Fachmann kann die Erfindung nicht ohne unzumutbaren Aufwand nacharbeiten, weil weder Messmethode noch Ausführungsbeispiele vorhanden sind. Zusätzlich kann Art. 84 EPÜ (Klarheit/Stützung) greifen, weil der Begriff 'verbesserter Schmelzpunkt' relativ und unklar ist.",
      "Ausschließlich Art. 56 EPÜ (erfinderische Tätigkeit), weil kein technischer Effekt nachgewiesen ist.",
      "Kein Einwand — Ausführungsbeispiele sind nach R. 42 EPÜ optional.",
    ],
    answer: 1,
    explanation:
      "Art. 83 EPÜ und Art. 84 EPÜ können kumulativ einschlägig sein: Art. 83 EPÜ greift, wenn der Fachmann die Erfindung nicht nacharbeiten kann — hier fehlen Ausführungsbeispiele und Messmethode, was zu unzumutbarem Aufwand führt. Art. 84 EPÜ greift, weil der Begriff 'verbesserter Schmelzpunkt' relativ ist (verbesserter Schmelzpunkt im Vergleich zu was?) und der Anspruch damit unklar ist. Beide Einwände sind berechtigt und häufig kombiniert anzutreffen. Art. 84 EPÜ allein würde nicht ausreichen, wenn die Nacharbeitbarkeit grundsätzlich unklar ist.",
    sourceIds: ["art83epc", "art84epc"],
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["eqe", "paperf", "ausfuehrbarkeit", "art83", "art84", "fallkonstellation"],
  }),

  // ─── Art. 84 EPÜ — Klarheit + Stützung ───────────────────────────────────

  // 011 — single, level 1: Art. 84 kein Einspruchsgrund
  makeSingle(sources, {
    id: "eqe-paperf-011",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Art. 84 EPÜ Klarheit",
    title: "Art. 84 EPÜ: Kein selbstständiger Einspruchsgrund",
    prompt:
      "Ein Einsprechender möchte ein Patent allein wegen mangelnder Klarheit der Ansprüche (Art. 84 EPÜ) anfechten. Ist dies nach dem EPÜ möglich?",
    options: [
      "Ja — Art. 84 EPÜ ist ein Einspruchsgrund nach Art. 100 EPÜ.",
      "Nein — Art. 84 EPÜ (Klarheit und Stützung durch die Beschreibung) ist kein selbstständiger Einspruchsgrund; Art. 100 EPÜ nennt ihn nicht. Klarheitsmängel können im Einspruchsverfahren nur angegriffen werden, soweit sie durch eine Änderung nach der Erteilung eingeführt wurden (G 3/14).",
      "Ja, aber nur wenn die Unklarheit zugleich einen Mangel nach Art. 83 EPÜ begründet.",
      "Nein, weil Klarheitsfragen ausschließlich vom nationalen Gericht im Verletzungsrechtsstreit geprüft werden.",
    ],
    answer: 1,
    explanation:
      "Art. 84 EPÜ (Ansprüche müssen klar, knapp und durch die Beschreibung gestützt sein) gilt als Prüfungs- und Einwandsmaßstab im Erteilungsverfahren. Art. 100 EPÜ, der die Einspruchsgründe abschließend auflistet, nennt Art. 84 EPÜ nicht. Mangelnde Klarheit ist daher grundsätzlich kein Einspruchsgrund. Die Große Beschwerdekammer hat in G 3/14 klargestellt, dass Klarheitsmängel im Einspruchsverfahren nur angegriffen werden können, soweit sie durch eine im Einspruchs- oder Beschwerdeverfahren vorgenommene Änderung neu entstanden sind.",
    sourceIds: ["art84epc"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "paperf", "klarheit", "art84", "einspruch"],
  }),

  // 012 — single, level 2: Stützung durch die Beschreibung
  makeSingle(sources, {
    id: "eqe-paperf-012",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Art. 84 EPÜ Stützung",
    title: "Stützung durch die Beschreibung (Art. 84 S. 2 EPÜ)",
    prompt:
      "Ein Anspruch beansprucht 'alle Polymere mit einem Molekulargewicht von 1.000 bis 1.000.000 Dalton'. Die Beschreibung offenbart nur ein Polymer mit einem Molekulargewicht von 50.000 Dalton und ein positives Beispiel. Liegt ein Mangel nach Art. 84 EPÜ vor?",
    options: [
      "Nein — wenn das Beispiel in der Beschreibung funktioniert, ist der gesamte beanspruchte Bereich automatisch gestützt.",
      "Ja — ein Anspruch ist nach Art. 84 S. 2 EPÜ durch die Beschreibung zu stützen. Beansprucht ein Anspruch einen breiten Bereich, muss die Beschreibung nachweisen, dass die Erfindung im gesamten beanspruchten Bereich reproduzierbar ist. Ein einzelnes Beispiel genügt hier nicht, um den gesamten Bereich von 1.000 bis 1.000.000 Dalton zu stützen.",
      "Nein — Art. 84 EPÜ betrifft nur die sprachliche Klarheit, nicht die Stützung durch Beispiele.",
      "Ja, aber nur wenn der Anspruch als Verwendungsanspruch formuliert ist.",
    ],
    answer: 1,
    explanation:
      "Art. 84 S. 2 EPÜ: Die Ansprüche müssen durch die Beschreibung gestützt werden. Die Stützung muss den gesamten beanspruchten Bereich abdecken. Ein einzelnes Beispiel genügt dann nicht, wenn der Anspruch einen breiten Bereich beansprucht und die Beschreibung nicht zeigt, dass der beanspruchte Bereich als Ganzes auf der erfinderischen Idee beruht. Dieses Problem tritt häufig bei breiten Bereichsansprüchen auf ('overly broad claims') und korrespondiert oft mit einem gleichzeitigen Art.-83-EPÜ-Mangel.",
    sourceIds: ["art84epc"],
    levelNumber: 2,
    errorCategory: "Subsumtion",
    skillTags: ["eqe", "paperf", "stuetzung", "art84", "fallkonstellation"],
  }),

  // ─── Art. 123(2) / Art. 123(3) EPÜ — Erweiterung ────────────────────────

  // 013 — single, level 1: Goldstandard Art. 123(2)
  makeSingle(sources, {
    id: "eqe-paperf-013",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Art. 123(2) EPÜ Erweiterung",
    title: "Art. 123(2) EPÜ: Goldstandard der unzulässigen Erweiterung",
    prompt:
      "Was ist der 'Goldstandard' für die Prüfung, ob eine Änderung nach Art. 123(2) EPÜ unzulässig ist?",
    options: [
      "Eine Änderung ist unzulässig, wenn sie inhaltlich über das hinausgeht, was ein Fachmann als naheliegend erachten würde.",
      "Eine Änderung ist unzulässig, wenn der geänderte Gegenstand nicht unmittelbar und eindeutig aus der ursprünglichen Offenbarung (Anmeldung in der eingereichten Fassung) ableitbar ist.",
      "Eine Änderung ist nur dann unzulässig, wenn sie den Schutzbereich nach Art. 69 EPÜ erweitert.",
      "Eine Änderung ist unzulässig, wenn sie nach der Erteilung vorgenommen wird und kein Einspruchsverfahren anhängig ist.",
    ],
    answer: 1,
    explanation:
      "Der von der EPA-Rechtsprechung entwickelte 'Goldstandard' für Art. 123(2) EPÜ lautet: Eine Änderung der Anmeldung oder des Patents ist nur dann zulässig, wenn der geänderte Gegenstand unmittelbar und eindeutig ('directly and unambiguously') aus der Europäischen Patentanmeldung in der eingereichten Fassung für den Fachmann ableitbar ist. Nicht ausreichend ist eine Ableitbarkeit, die nur durch Schlussfolgerungen oder Extrapolation möglich wäre. Dieser Standard gilt für alle Änderungen — im Prüfungsverfahren, im Einspruchsverfahren und in der Beschwerdeinstanz.",
    sourceIds: ["art123epc"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "paperf", "erweiterung", "art123", "goldstandard"],
  }),

  // 014 — single, level 3 (Fallkonstellation): Art. 123(2) anwenden
  makeSingle(sources, {
    id: "eqe-paperf-014",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Art. 123(2) EPÜ Erweiterung",
    title: "Fall: Streichung eines Merkmals aus dem Anspruch",
    prompt:
      "Die eingereichte Anmeldung offenbart: 'Fahrzeug mit Elektromotor, Bremsanlage und integriertem Kühlsystem.' Anspruch 1 in der eingereichten Fassung beansprucht alle drei Merkmale. Im Prüfungsverfahren streicht der Anmelder 'integriertes Kühlsystem' aus Anspruch 1. Ist diese Änderung nach Art. 123(2) EPÜ zulässig?",
    options: [
      "Ja — die Streichung eines Merkmals aus einem Anspruch ist stets zulässig, weil sie den Schutzbereich einschränkt.",
      "Nein — jede Streichung eines Merkmals aus einem Anspruch verstößt automatisch gegen Art. 123(2) EPÜ.",
      "Nur zulässig, wenn in der Beschreibung ein Ausführungsbeispiel eines Fahrzeugs ohne integriertes Kühlsystem offenbart ist; andernfalls wäre der geänderte Anspruch nicht unmittelbar und eindeutig aus der ursprünglichen Offenbarung ableitbar.",
      "Zulässig, weil die Beschreibung das Kühlsystem nur als 'bevorzugte Ausführungsform' beschreibt.",
    ],
    answer: 2,
    explanation:
      "Die Streichung eines Merkmals aus einem Anspruch erweitert den Schutzbereich (der Anspruch erfasst nun auch Fahrzeuge ohne Kühlsystem) und ist daher an Art. 123(2) EPÜ zu messen. Zulässig ist die Streichung nur, wenn die Beschreibung in der eingereichten Fassung auch Ausführungsformen ohne das gestrichene Merkmal offenbart — sodass die erweiterte Anmeldung 'unmittelbar und eindeutig' aus der Offenbarung ableitbar ist. Ist das Kühlsystem in allen Ausführungsformen als wesentliches Merkmal beschrieben, ist die Streichung unzulässig. Die Formulierung 'bevorzugte Ausführungsform' allein entbindet nicht von dieser Prüfung.",
    sourceIds: ["art123epc"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["eqe", "paperf", "erweiterung", "art123", "fallkonstellation"],
  }),

  // 015 — single, level 2: Art. 123(3) Schutzbereichserweiterung nach Erteilung
  makeSingle(sources, {
    id: "eqe-paperf-015",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Art. 123(3) EPÜ Schutzbereich nach Erteilung",
    title: "Art. 123(3) EPÜ: Schutzbereichserweiterung nach Erteilung verboten",
    prompt:
      "Was verbietet Art. 123(3) EPÜ nach der Erteilung des Patents?",
    options: [
      "Jede Änderung, die den Schutzbereich des erteilten Patents nach Art. 69 EPÜ erweitert — unabhängig davon, ob die Änderung durch die ursprüngliche Offenbarung gedeckt ist.",
      "Jede Änderung der Beschreibung, die über den ursprünglichen Offenbarungsgehalt hinausgeht.",
      "Jede Änderung, die nach dem Einspruchsverfahren vorgenommen wird.",
      "Nur solche Änderungen, die den Anspruchswortlaut erweitern, nicht aber äquivalente Varianten.",
    ],
    answer: 0,
    explanation:
      "Art. 123(3) EPÜ: Nach der Erteilung darf das europäische Patent nicht in der Weise geändert werden, dass sein Schutzbereich (gemessen nach Art. 69 EPÜ) erweitert wird. Dieses Verbot ist absolut und gilt unabhängig davon, ob die Änderung durch Art. 123(2) EPÜ zulässig wäre (d.h. durch die ursprüngliche Offenbarung gedeckt ist). Art. 123(2)/(3) EPÜ können daher eine 'Inescapable Trap' bilden: Eine Änderung, die nötig wäre, um Art. 123(2) EPÜ zu erfüllen, könnte Art. 123(3) EPÜ verletzen, und umgekehrt.",
    sourceIds: ["art123epc"],
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["eqe", "paperf", "erweiterung", "art123", "nach-erteilung"],
  }),

  // 016 — single, level 3 (Fallkonstellation): Inescapable Trap
  makeSingle(sources, {
    id: "eqe-paperf-016",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Art. 123(2)/(3) EPÜ Inescapable Trap",
    title: "Fall: Die Inescapable Trap (Art. 123(2)/(3) EPÜ)",
    prompt:
      "Ein erteiltes Patent enthält in Anspruch 1 ein Merkmal X, das in der eingereichten Anmeldung nicht offenbart war (Versehen des Anmelders). Im Einspruchsverfahren möchte der Patentinhaber Merkmal X streichen. Die Streichung würde jedoch den Schutzbereich gegenüber dem erteilten Patent erweitern. Welche Situation liegt vor?",
    options: [
      "Der Patentinhaber kann ohne Weiteres Merkmal X streichen, weil eine Einschränkung des Anspruchs immer zulässig ist.",
      "Der Patentinhaber ist in der 'Inescapable Trap': Behält er Merkmal X, verletzt der Anspruch Art. 123(2) EPÜ (unzulässige Erweiterung gegenüber der Offenbarung); streicht er es, verletzt er Art. 123(3) EPÜ (Schutzbereichserweiterung). Das Patent ist in beiden Alternativen widerruflich.",
      "Der Patentinhaber kann Merkmal X streichen, weil Art. 123(3) EPÜ nur bei Änderungen gilt, die nach dem Einspruchsverfahren vorgenommen werden.",
      "Die Situation ist durch Einreichung eines Hilfsantrags mit einem anderen Merkmal Y lösbar, das Art. 123(2) und (3) gleichzeitig erfüllt.",
    ],
    answer: 1,
    explanation:
      "Die 'Inescapable Trap' entsteht, wenn ein Anspruch nach der Erteilung ein Merkmal enthält, das nicht in der ursprünglichen Offenbarung vorhanden war (Verstoß gegen Art. 123(2) EPÜ), und das Streichen dieses Merkmals den Schutzbereich des erteilten Anspruchs erweitern würde (Verstoß gegen Art. 123(3) EPÜ). In dieser Situation ist das Patent nach ständiger Rechtsprechung der Beschwerdekammern (z.B. T 1149/97) in beiden Fassungen nicht haltbar und muss widerrufen werden. Option D ist insofern unvollständig, als ein Hilfsantrag tatsächlich ein Ausweg sein kann — wenn ein Merkmal gefunden wird, das beide Normen gleichzeitig erfüllt; aber die Fragestellung zielt auf die Grundsituation.",
    sourceIds: ["art123epc"],
    levelNumber: 3,
    errorCategory: "Analyse",
    skillTags: ["eqe", "paperf", "erweiterung", "art123", "inescapable-trap", "fallkonstellation"],
  }),

  // ─── Art. 76 EPÜ — Teilanmeldung ─────────────────────────────────────────

  // 017 — single, level 1: Grundregel Teilanmeldung
  makeSingle(sources, {
    id: "eqe-paperf-017",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Art. 76 EPÜ Teilanmeldung",
    title: "Teilanmeldung: Zeitrang und Voraussetzungen (Art. 76 EPÜ)",
    prompt:
      "Welche Aussage zur Teilanmeldung nach Art. 76 EPÜ ist korrekt?",
    options: [
      "Die Teilanmeldung erhält als Anmeldetag den Tag ihrer eigenen Einreichung; ein früherer Zeitrang wird nicht übertragen.",
      "Die Teilanmeldung erhält den Anmeldetag der Stammanmeldung als Zeitrang; die Stammanmeldung muss zum Zeitpunkt der Einreichung der Teilanmeldung noch anhängig sein, und der Inhalt der Teilanmeldung darf nicht über den Inhalt der Stammanmeldung hinausgehen.",
      "Die Teilanmeldung ist nur zulässig, wenn die Stammanmeldung bereits erteilt wurde.",
      "Die Teilanmeldung muss denselben Anmelder wie die Stammanmeldung haben und ist nur bis zur Veröffentlichung der Stammanmeldung möglich.",
    ],
    answer: 1,
    explanation:
      "Art. 76(1) EPÜ: Eine Teilanmeldung erhält als Anmeldetag den Anmeldetag der Stammanmeldung (Zeitrang wird übertragen). Voraussetzung ist, dass die Stammanmeldung zum Zeitpunkt der Einreichung der Teilanmeldung noch anhängig ist (nicht zurückgezogen oder rechtskräftig zurückgewiesen). Der Inhalt der Teilanmeldung darf nicht über den Inhalt der Stammanmeldung in ihrer eingereichten Fassung hinausgehen (Art. 76(1) S. 2 EPÜ i.V.m. Art. 123(2) EPÜ). Die Teilanmeldung kann eingebracht werden, bis die Stammanmeldung erteilt oder endgültig zurückgewiesen wird.",
    sourceIds: ["art76epc"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "paperf", "teilanmeldung", "art76"],
  }),

  // 018 — single, level 3 (Fallkonstellation): Teilanmeldung über Stamm hinaus
  makeSingle(sources, {
    id: "eqe-paperf-018",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Art. 76 EPÜ Teilanmeldung",
    title: "Fall: Teilanmeldung mit neuem Gegenstand",
    prompt:
      "Anmelder C reicht eine Teilanmeldung ein, die eine Ausführungsform beansprucht, die zwar technisch mit der Stammanmeldung verwandt ist, aber in keiner Ausführungsform der Stammanmeldung in der eingereichten Fassung offenbart wurde. Was ist die Folge?",
    options: [
      "Die Teilanmeldung ist zulässig, weil technische Verwandtschaft ausreicht.",
      "Die Teilanmeldung verstößt gegen Art. 76(1) S. 2 EPÜ i.V.m. Art. 123(2) EPÜ: Sie geht über den Inhalt der Stammanmeldung hinaus. Das EPA wird einen entsprechenden Einwand erheben; der neue Gegenstand muss gestrichen oder auf den in der Stammanmeldung offenbarten Gehalt zurückgeführt werden.",
      "Die Teilanmeldung erhält zwar nicht den Zeitrang der Stammanmeldung, ist aber als eigenständige Neuanmeldung zulässig.",
      "Die Stammanmeldung wird automatisch ungültig, wenn die Teilanmeldung über ihren Inhalt hinausgeht.",
    ],
    answer: 1,
    explanation:
      "Art. 76(1) S. 2 EPÜ schreibt vor, dass eine Teilanmeldung nicht über den Inhalt der Stammanmeldung in ihrer eingereichten Fassung hinausgehen darf. Ein neuer Gegenstand, der in der Stammanmeldung nicht offenbart wurde, ist ein Verstoß gegen Art. 76(1) EPÜ i.V.m. Art. 123(2) EPÜ. Das EPA erhebt einen formalen Einwand; das Problem kann behoben werden, indem der beanspruchte Gegenstand auf das beschränkt wird, was die Stammanmeldung offenbart. Eine Teilanmeldung kann nie mehr offenbaren als ihre Stammanmeldung.",
    sourceIds: ["art76epc", "art123epc"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["eqe", "paperf", "teilanmeldung", "art76", "fallkonstellation"],
  }),

  // ─── Art. 87-89 EPÜ — Priorität ──────────────────────────────────────────

  // 019 — cloze, level 1: 12-Monats-Prioritätsfrist
  makeCloze(sources, {
    id: "eqe-paperf-019",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Art. 87 EPÜ Prioritätsfrist",
    title: "Prioritätsfrist: 12 Monate (Cloze)",
    prompt:
      "Nach Art. 87(1) EPÜ beträgt die Frist zur Inanspruchnahme einer Priorität aus einer Erstanmeldung ___ Monate ab dem Anmeldetag der Erstanmeldung.",
    options: ["6", "12", "18", "30"],
    answer: 1,
    explanation:
      "Art. 87(1) EPÜ: Die Prioritätsfrist beträgt zwölf Monate ab dem Anmeldetag der Erstanmeldung. Innerhalb dieser Frist muss die europäische Anmeldung eingereicht werden, um die Priorität wirksam zu beanspruchen. Die 12-Monate-Frist entspricht der Pariser Verbandsübereinkunft (Art. 4C PVÜ). Eine Verlängerung ist grundsätzlich nicht möglich; die Frist ist von der Weiterbehandlung nach Art. 121 EPÜ ausgeschlossen (R. 135(2) EPÜ). Bei Versäumnis kann nur unter engen Voraussetzungen Wiedereinsetzung begehrt werden.",
    sourceIds: ["art87epc"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "paperf", "prioritaet", "art87", "cloze"],
  }),

  // 020 — single, level 2: same invention (G 2/98)
  makeSingle(sources, {
    id: "eqe-paperf-020",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Art. 87 EPÜ Priorität: Dieselbe Erfindung",
    title: "Priorität: 'Dieselbe Erfindung' (G 2/98)",
    prompt:
      "Die Große Beschwerdekammer hat in G 2/98 den Maßstab für 'dieselbe Erfindung' im Sinne von Art. 87(1) EPÜ festgelegt. Welcher Goldstandard gilt?",
    options: [
      "Der beanspruchte Gegenstand muss identisch mit dem in der Prioritätsanmeldung beanspruchten Gegenstand sein.",
      "Der beanspruchte Gegenstand muss unmittelbar und eindeutig aus der Prioritätsanmeldung in ihrer Gesamtheit ableitbar sein — einschließlich Beschreibung, Ansprüche und Zeichnungen. Erweiterungen oder neue Merkmale gegenüber der Prioritätsanmeldung führen zum Verlust des Prioritätsrechts für diesen Gegenstand.",
      "Der beanspruchte Gegenstand muss in der Prioritätsanmeldung ausdrücklich als Anspruch formuliert sein.",
      "Es genügt, dass eine technisch verwandte Erfindung in der Prioritätsanmeldung beschrieben ist.",
    ],
    answer: 1,
    explanation:
      "G 2/98 (Große Beschwerdekammer): Der Maßstab für 'dieselbe Erfindung' (same invention) im Sinne von Art. 87(1) EPÜ ist identisch mit dem Goldstandard des Art. 123(2) EPÜ: Der beanspruchte Gegenstand muss unmittelbar und eindeutig aus der Prioritätsanmeldung als Ganzes für den Fachmann ableitbar sein. Ein Anspruch kann nur dann wirksam Priorität genießen, wenn sein gesamter Gegenstand in der Prioritätsanmeldung offenbart ist. Ist ein Merkmal des Anspruchs neu gegenüber der Prioritätsanmeldung, geht die Priorität für diesen Gegenstand verloren.",
    sourceIds: ["art87epc"],
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["eqe", "paperf", "prioritaet", "art87", "g2-98", "same-invention"],
  }),

  // 021 — single, level 2: Teilpriorität / G 1/15
  makeSingle(sources, {
    id: "eqe-paperf-021",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Art. 88 EPÜ Teilpriorität / G 1/15",
    title: "Teilpriorität und Mehrfachpriorität (Art. 88 EPÜ / G 1/15)",
    prompt:
      "Ein Anspruch beansprucht 'ein Polymer mit einem Molekulargewicht im Bereich von 1.000 bis 1.000.000 Dalton'. Die Prioritätsanmeldung offenbart nur Polymere mit einem Molekulargewicht von 1.000 bis 100.000 Dalton. Welche Konsequenz hat G 1/15?",
    options: [
      "Der Anspruch verliert vollständig die Priorität, weil er über die Prioritätsanmeldung hinausgeht.",
      "Nach G 1/15 kann einem Anspruch Teilpriorität zukommen: Der Teilbereich von 1.000 bis 100.000 Dalton genießt die Priorität der Erstanmeldung; der Teilbereich von 100.000 bis 1.000.000 Dalton hat keinen Prioritätsanspruch und wird durch spätere Veröffentlichungen als Stand der Technik behandelt.",
      "Der gesamte Anspruch genießt die Priorität, wenn die Prioritätsanmeldung zumindest einen Teil des beanspruchten Bereichs offenbart.",
      "Teilprioritäten sind nach EPÜ grundsätzlich unzulässig; ein Anspruch ist entweder vollständig prioritätsberechtigt oder gar nicht.",
    ],
    answer: 1,
    explanation:
      "G 1/15 (Große Beschwerdekammer): Einem Anspruch, dessen Gegenstand über den in der Prioritätsanmeldung offenbarten Bereich hinausgeht, kann 'Teilpriorität' (partial priority) zukommen. Der Teilbereich, der unmittelbar und eindeutig aus der Prioritätsanmeldung ableitbar ist (hier: 1.000–100.000 Dalton), genießt die Priorität; der nicht offenbarte Teilbereich (100.000–1.000.000 Dalton) hat keinen Prioritätsschutz. Dies ist besonders bei Bereichsansprüchen und 'OR-Ansprüchen' relevant und vermeidet, dass spätere Veröffentlichungen des Anmelders selbst als Stand der Technik nach Art. 54(3) EPÜ schaden.",
    sourceIds: ["art88epc", "art87epc"],
    levelNumber: 2,
    errorCategory: "Ausnahme",
    skillTags: ["eqe", "paperf", "prioritaet", "art88", "teilprioritaet", "g1-15"],
  }),

  // 022 — single, level 3 (Fallkonstellation): Priorität und Neuheit (Art. 89 EPÜ)
  makeSingle(sources, {
    id: "eqe-paperf-022",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Art. 89 EPÜ Prioritätswirkung",
    title: "Fall: Priorität und Neuheitsschädlichkeit einer Zwischenveröffentlichung",
    prompt:
      "Anmelder D reicht am 1. Januar (Jahr 1) eine erste Anmeldung (FR) ein. Am 15. Juli (Jahr 1) veröffentlicht ein Dritter eine Schrift, die denselben Gegenstand offenbart. Am 15. Dezember (Jahr 1) reicht D beim EPA eine europäische Anmeldung ein und beansprucht wirksam die Priorität. Ist die Drittveröffentlichung vom 15. Juli für die Neuheitsprüfung relevant?",
    options: [
      "Ja — die Drittveröffentlichung ist Stand der Technik nach Art. 54 EPÜ und neuheitsschädlich.",
      "Nein — nach Art. 89 EPÜ gilt der Prioritätstag (1. Januar, Jahr 1) als Anmeldetag der europäischen Anmeldung für die Beurteilung von Neuheit und erfinderischer Tätigkeit. Die Drittveröffentlichung vom 15. Juli liegt nach dem Prioritätstag und ist daher kein Stand der Technik.",
      "Die Drittveröffentlichung ist nur für die erfinderische Tätigkeit, nicht für die Neuheit relevant.",
      "Es kommt darauf an, ob die Drittveröffentlichung vor oder nach der Veröffentlichung der europäischen Anmeldung erfolgte.",
    ],
    answer: 1,
    explanation:
      "Art. 89 EPÜ: Die Inanspruchnahme des Prioritätsrechts hat zur Folge, dass der Prioritätstag (hier: 1. Januar, Jahr 1) als Anmeldetag der europäischen Patentanmeldung für die Beurteilung von Neuheit (Art. 54 EPÜ) und erfinderischer Tätigkeit (Art. 56 EPÜ) gilt. Veröffentlichungen, die nach dem Prioritätstag aber vor dem tatsächlichen Anmeldetag der europäischen Anmeldung erschienen sind, werden daher nicht als Stand der Technik gewertet. Die Drittveröffentlichung vom 15. Juli ist mithin irrelevant.",
    sourceIds: ["art87epc", "art88epc"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["eqe", "paperf", "prioritaet", "art89", "neuheit", "fallkonstellation"],
  }),

  // ─── Art. 80 + R. 40 — Anmeldetag vs. vollständige Anmeldung ─────────────

  // 023 — single, level 2: nachgereichte Zeichnungen
  makeSingle(sources, {
    id: "eqe-paperf-023",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Art. 80 / R. 40 EPÜ Nachreichung",
    title: "Nachgereichte Zeichnungen: Wirkung auf den Anmeldetag",
    prompt:
      "Eine europäische Anmeldung verweist in der Beschreibung auf Zeichnungen, die bei der Einreichung fehlen. Das EPA teilt trotzdem einen Anmeldetag zu. Was ist die Konsequenz, wenn die Zeichnungen später nachgereicht werden?",
    options: [
      "Die Zeichnungen werden rückwirkend auf den ursprünglichen Anmeldetag aufgenommen, wenn der Anmelder sie innerhalb von 2 Monaten nach Aufforderung einreicht.",
      "Wenn der Anmelder die Zeichnungen fristgerecht nachreicht, wird der Anmeldetag auf den Tag der Nachreichung verschoben, sofern der Anmelder nicht erklärt, dass auf die Zeichnungen verzichtet wird.",
      "Fehlende Zeichnungen führen stets zum Verlust des Anmeldetags; eine Nachreichung ist nicht möglich.",
      "Das EPA teilt den Anmeldetag ohne Zeichnungen zu und ignoriert den Verweis auf Zeichnungen dauerhaft.",
    ],
    answer: 1,
    explanation:
      "R. 56 EPÜ: Verweist die Beschreibung auf Zeichnungen, die nicht eingereicht wurden, teilt das EPA zwar trotzdem einen Anmeldetag zu (weil Zeichnungen kein Anmeldetag-Erfordernis nach R. 40(1) EPÜ sind). Reicht der Anmelder die Zeichnungen jedoch nach, wird der Anmeldetag auf den Tag der Nachreichung verschoben — es sei denn, der Anmelder erklärt, auf die Zeichnungen zu verzichten, oder die fehlenden Zeichnungen werden aus einer Prioritätsanmeldung entnommen. Diese Regelung ist ein häufiger Paper-F-Fallstrick.",
    sourceIds: ["r40epc", "r56epc", "art80epc"],
    levelNumber: 2,
    errorCategory: "Subsumtion",
    skillTags: ["eqe", "paperf", "anmeldetag", "r40", "zeichnungen", "nachreichung"],
  }),

  // 024 — single, level 3 (Fallkonstellation): Kombination Anmeldetag / Priorität / Zeichnungen
  makeSingle(sources, {
    id: "eqe-paperf-024",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Art. 80 / R. 40 / Art. 87 EPÜ Fallkonstellation",
    title: "Fall: Zeichnungsnachreichung bei Prioritätsanmeldung mit Zeichnungen",
    prompt:
      "Anmelderin E reicht am 1. März ohne Zeichnungen ein; die Beschreibung verweist auf Zeichnungen. Die Prioritätsanmeldung vom 15. Februar des Vorjahres enthält dieselben Zeichnungen. E reicht die Zeichnungen am 10. März nach und erklärt, sie seien aus der Prioritätsanmeldung entnommen. Welcher Anmeldetag gilt?",
    options: [
      "10. März — weil die Zeichnungen erst am 10. März nachgereicht wurden.",
      "1. März — weil die nachgereichten Zeichnungen aus der Prioritätsanmeldung entnommen sind und E dies erklärt hat; in diesem Fall wird der Anmeldetag nicht verschoben.",
      "15. Februar des Vorjahres (Prioritätstag) — weil alle Teile der Anmeldung aus der Prioritätsanmeldung stammen.",
      "Das EPA muss zunächst prüfen, ob die Zeichnungen in der Prioritätsanmeldung vollständig sind, bevor ein Anmeldetag zugeteilt wird.",
    ],
    answer: 1,
    explanation:
      "R. 56 Abs. 3 EPÜ sieht eine Ausnahme von der Anmeldetag-Verschiebung vor: Wenn der Anmelder erklärt, dass die nachgereichten Zeichnungen vollständig aus der Prioritätsanmeldung entnommen sind, und er diese Erklärung fristgerecht abgibt, bleibt der ursprüngliche Anmeldetag (1. März) erhalten. Der Anmeldetag wird nicht auf den Tag der Nachreichung verschoben. Diese Ausnahme soll verhindern, dass Anmelder durch eine kleine Formalie ihren Prioritätsschutz verlieren.",
    sourceIds: ["r40epc", "r56epc", "art80epc", "art87epc"],
    levelNumber: 3,
    errorCategory: "Ausnahme",
    skillTags: ["eqe", "paperf", "anmeldetag", "r40", "prioritaet", "fallkonstellation"],
  }),

  // ─── ORDERING — Prüfungsschemata ─────────────────────────────────────────

  // 025 — ordering, level 2: Prüfung Anmeldetagserfordernisse R. 40
  makeOrdering(sources, {
    id: "eqe-paperf-025",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — R. 40 EPÜ Prüfungsschema",
    title: "Ordering: Prüfung der Anmeldetagserfordernisse (R. 40 EPÜ)",
    prompt:
      "Bringen Sie die folgenden Prüfungsschritte für die Zuerkennung eines Anmeldetags nach R. 40(1) EPÜ in die richtige Reihenfolge.",
    orderItems: [
      "Liegt eine Angabe vor, dass ein europäisches Patent beantragt wird? (R. 40(1)(a) EPÜ)",
      "Sind Angaben vorhanden, die die Identität des Anmelders feststellen lassen? (R. 40(1)(b) EPÜ)",
      "Ist eine Beschreibung oder eine Bezugnahme auf eine frühere Anmeldung mit Aktenzeichen eingereicht? (R. 40(1)(c) EPÜ)",
      "Wenn alle drei Erfordernisse erfüllt: Anmeldetag zuteilen (Art. 80 EPÜ).",
      "Wenn Erfordernisse fehlen: Aufforderung zur Ergänzung innerhalb der gesetzten Frist; andernfalls gilt die Anmeldung als nicht eingereicht.",
    ],
    explanation:
      "R. 40(1) EPÜ nennt drei kumulative Minimalerfordernisse: (a) Hinweis auf ein europäisches Patent, (b) Anmelderidentität, (c) Beschreibung oder Bezugnahme auf eine frühere Anmeldung. Diese drei Punkte werden zunächst sequenziell geprüft. Sind alle erfüllt, wird der Anmeldetag (Art. 80 EPÜ) zugeteilt. Fehlt mindestens eines, ergeht eine Aufforderung zur Nachbesserung (R. 55 EPÜ); bei Nichtbesserung gilt die Anmeldung als nicht eingereicht. Wichtig: Ansprüche sind kein Anmeldetag-Erfordernis.",
    sourceIds: ["r40epc", "art80epc"],
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["eqe", "paperf", "anmeldetag", "r40", "ordering", "schema"],
  }),

  // 026 — ordering, level 2: Aufbau eines unabhängigen Anspruchs (R. 43)
  makeOrdering(sources, {
    id: "eqe-paperf-026",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — R. 43 EPÜ Anspruchsaufbau",
    title: "Ordering: Aufbau eines unabhängigen Anspruchs nach R. 43 EPÜ",
    prompt:
      "Bringen Sie die folgenden Elemente eines unabhängigen Patentanspruchs in die richtige strukturelle Reihenfolge (Entwurfsschritte bei Paper F).",
    orderItems: [
      "Anspruchsbezeichnung (Kategorie): Erzeugnisanspruch, Verfahrensanspruch oder Verwendungsanspruch festlegen.",
      "Oberbegriff formulieren: bekannte Merkmale der Erfindung benennen, die dem nächsten Stand der Technik entsprechen.",
      "Kennzeichnenden Teil einleiten: 'dadurch gekennzeichnet, dass' (oder gleichwertige Wendung).",
      "Neue Merkmale im kennzeichnenden Teil formulieren: alle schutzwürdigen Unterschiedsmerkmale gegenüber dem Stand der Technik angeben.",
      "Klarheit und Stützung prüfen: sicherstellen, dass der Anspruch klar, knapp und durch die Beschreibung gestützt ist (Art. 84 EPÜ).",
      "Art. 123(2) EPÜ prüfen: sicherstellen, dass der Anspruch nicht über die ursprüngliche Offenbarung hinausgeht.",
    ],
    explanation:
      "Nach R. 43(1) EPÜ besteht ein unabhängiger Anspruch aus einem Oberbegriff (bekannte Merkmale) und einem kennzeichnenden Teil ('dadurch gekennzeichnet, dass') mit den neuen Merkmalen. In Paper F ist es entscheidend, diesen Aufbau systematisch einzuhalten: Kategorie bestimmen → Oberbegriff (Stand der Technik) → Einleitung des kennzeichnenden Teils → neue Merkmale → Klarheitsprüfung nach Art. 84 EPÜ → Prüfung auf unzulässige Erweiterung nach Art. 123(2) EPÜ.",
    sourceIds: ["r43epc", "art84epc", "art123epc"],
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["eqe", "paperf", "ansprueche", "r43", "ordering", "schema"],
  }),

  // ─── CLOZE — Goldstandard Art. 123(2) ────────────────────────────────────

  // 027 — cloze, level 1: Goldstandard-Formel Art. 123(2)
  makeCloze(sources, {
    id: "eqe-paperf-027",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Art. 123(2) EPÜ Goldstandard",
    title: "Goldstandard-Formel Art. 123(2) EPÜ (Cloze)",
    prompt:
      "Eine Änderung nach Art. 123(2) EPÜ ist nur dann zulässig, wenn der geänderte Gegenstand ___ aus der Europäischen Patentanmeldung in der eingereichten Fassung für den Fachmann ableitbar ist.",
    options: [
      "naheliegend und sinngemäß",
      "unmittelbar und eindeutig",
      "mittelbar und plausibel",
      "implizit und gleichwertig",
    ],
    answer: 1,
    explanation:
      "Der Goldstandard des Art. 123(2) EPÜ lautet: 'unmittelbar und eindeutig' (directly and unambiguously). Eine Änderung darf nicht über das hinausgehen, was ein Fachmann dieser Offenbarung direkt und eindeutig entnehmen kann — ohne Schlussfolgerungen, Extrapolationen oder das Einbringen von Fachwissen. Dieser Standard gilt identisch auch für Art. 87(1) EPÜ (Priorität: same invention) und ist das zentrale Prüfungskriterium in Paper F.",
    sourceIds: ["art123epc"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["eqe", "paperf", "erweiterung", "art123", "goldstandard", "cloze"],
  }),

  // 028 — multi, level 3 (Fallkonstellation): Gesamtschau Paper F
  makeMulti(sources, {
    id: "eqe-paperf-028",
    pathId,
    reviewStatus: "reviewed",
    topic: "Paper F — Gesamtschau Formerfordernisse & Anspruchs-Drafting",
    title: "Paper-F-Gesamtfall: Mängel identifizieren (Mehrfachauswahl)",
    prompt:
      "Eine europäische Anmeldung wurde ohne Ansprüche eingereicht. Die Beschreibung fehlt ein Ausführungsbeispiel. Die Ansprüche werden zwei Monate nach Einreichung nachgereicht und beanspruchen in Anspruch 1 einen Gegenstand, der über die Beschreibung in der eingereichten Fassung hinausgeht. Welche der folgenden Aussagen treffen zu? (Mehrere Antworten möglich.)",
    options: [
      "Das Fehlen von Ansprüchen bei der Einreichung schadet dem Anmeldetag nicht (R. 40(1) EPÜ), wenn Beschreibung und Anmelderidentität vorhanden sind.",
      "Das Fehlen eines Ausführungsbeispiels begründet zwingend einen Art.-83-EPÜ-Mangel.",
      "Der nachgereichte Anspruch 1 kann einen Art.-123(2)-EPÜ-Mangel begründen, wenn er über die ursprüngliche Offenbarung hinausgeht.",
      "Ein Art.-84-EPÜ-Mangel (fehlende Stützung) kann neben einem Art.-123(2)-EPÜ-Mangel vorliegen, wenn der Anspruch einen Bereich beansprucht, der in der Beschreibung nicht abgedeckt ist.",
    ],
    answers: [0, 2, 3],
    explanation:
      "Option A ist richtig: R. 40(1) EPÜ verlangt keine Ansprüche am Anmeldetag. Option B ist falsch: Das Fehlen eines Ausführungsbeispiels begründet nicht zwingend einen Art.-83-EPÜ-Mangel — entscheidend ist, ob der Fachmann die Erfindung trotzdem ohne unzumutbaren Aufwand nacharbeiten kann; in manchen technischen Gebieten sind Beispiele nicht zwingend. Option C ist richtig: Ansprüche, die nach der Einreichung nachgereicht werden, müssen Art. 123(2) EPÜ genügen — Gegenstände, die nicht in der ursprünglichen Beschreibung offenbart sind, sind unzulässig. Option D ist richtig: Art. 84 EPÜ (Stützung) und Art. 123(2) EPÜ (Erweiterung) können parallel vorliegen und setzen ähnliche Prüfungsmaßstäbe an, sind aber eigenständige Einwände.",
    sourceIds: ["r40epc", "art83epc", "art84epc", "art123epc"],
    levelNumber: 3,
    errorCategory: "Analyse",
    skillTags: ["eqe", "paperf", "gesamtschau", "r40", "art83", "art84", "art123", "fallkonstellation"],
  }),
];
